import React, { useEffect, useState } from "react";
import { api, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { TrendingUp, Users, Boxes, Ticket, ShieldAlert, Coins, TriangleAlert, DoorOpen } from "lucide-react";

export default function OwnerCommandCenterPage() {
  const { branchId } = useAuth();
  const [data, setData] = useState(null);
  const [range, setRange] = useState(7);

  const load = async () => {
    try {
      const params = { days: range };
      if (branchId) params.branch_id = branchId;
      const { data } = await api.get("/owner/command-center", { params });
      setData(data);
    } catch (_) {}
  };
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, [branchId, range]);

  if (!data) return <div className="p-14 ink-subtle text-sm">Loading command center…</div>;

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="owner-command-center">
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">Owner · Command center</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">One glance.</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Cross-module read-only view. Everything the owner needs at 3am — sales, cash health, live head count, staff, inventory, events, pending approvals.
          </p>
        </div>
        <div className="flex items-center gap-1 border hairline-soft rounded-full p-1">
          {[1, 7, 30].map((d) => (
            <button key={d} onClick={() => setRange(d)}
                    data-testid={`occ-range-${d}`}
                    className={`h-8 px-4 rounded-full text-[10px] uppercase tracking-[0.24em] font-bold transition-all ${
                      range === d ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"
                    }`}>
              {d === 1 ? "24h" : `${d}d`}
            </button>
          ))}
        </div>
      </div>

      {/* Big number */}
      <div className="mt-14 animate-fade-up animate-fade-up-1">
        <div className="section-eyebrow">Sales</div>
        <div className="mt-3 display-heading text-6xl md:text-7xl ink mono" data-testid="occ-sales-total">
          {peso(data.sales.total)}
        </div>
        <div className="mt-3 tiny-label ink-subtle">
          <span className="mono ink">{peso(data.sales.cash)}</span> in cash · {data.sales.orders_paid} paid orders · {data.sales.orders_open} still open
        </div>
      </div>

      {/* Grid */}
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-fade-up animate-fade-up-2">
        <Card icon={DoorOpen} num=".01" title="Live door" testid="occ-entrance"
              main={`${data.entrance.live_head_count}${data.entrance.capacity ? ` / ${data.entrance.capacity}` : ""}`}
              sub={data.entrance.session_open ? "Session open" : "No open session"}
              link="/security"/>
        <Card icon={Users} num=".02" title="On the clock" testid="occ-labor"
              main={data.labor.on_clock}
              sub="Live staff count"
              link="/payroll"/>
        <Card icon={Boxes} num=".03" title="Stock value" testid="occ-inventory"
              main={peso(data.inventory.total_stock_value)}
              sub={`${data.inventory.low_stock} low-stock · wastage ${peso(data.inventory.wastage_cost)}`}
              link="/inventory/balances"/>
        <Card icon={Ticket} num=".04" title="Events" testid="occ-events"
              main={peso(data.events.revenue)}
              sub={`${data.events.upcoming} upcoming`}
              link="/events"/>
        <Card icon={ShieldAlert} num=".05" title="Approvals" testid="occ-approvals"
              main={data.approvals.pending}
              sub={data.approvals.pending > 0 ? "Need attention" : "All clear"}
              accent={data.approvals.pending > 0}
              link="/approvals"/>
        <Card icon={Coins} num=".06" title="Cash on hand" testid="occ-cash"
              main={peso(data.sales.cash)}
              sub="From payments in range"
              link="/finance/cash-sessions"/>
        <Card icon={TrendingUp} num=".07" title="Payment mix" testid="occ-methods"
              main={data.sales.by_method.length}
              sub={data.sales.by_method.slice(0,3).map((m) => `${m.method}: ${peso(m.amount)}`).join(" · ") || "—"}/>
        <Card icon={TriangleAlert} num=".08" title="Wastage cost" testid="occ-wastage"
              main={peso(data.inventory.wastage_cost)}
              sub={`In the last ${data.range_days}d`}
              accent={data.inventory.wastage_cost > 500}
              link="/inventory/wastage"/>
      </div>

      <div className="mt-14 tiny-label ink-subtle animate-fade-up animate-fade-up-3">
        Refreshed {new Date(data.generated_at).toLocaleTimeString("en-PH")} · window {data.range_days} day{data.range_days === 1 ? "" : "s"}
      </div>
    </div>
  );
}

function Card({ icon: Icon, num, title, main, sub, accent, link, testid }) {
  const inner = (
    <div className={`border hairline surface-card rounded-lg p-5 h-full transition-colors ${link ? "hover:border-foreground/50" : ""}`}>
      <div className="flex items-start justify-between">
        <Icon size={18} className="ink"/>
        <span className="section-num">{num}</span>
      </div>
      <div className="mt-4 tiny-label ink-subtle">{title}</div>
      <div className={`mt-2 mono text-3xl font-semibold ${accent ? "text-amber-500" : "ink"}`}>{main}</div>
      <div className="mt-2 tiny-label ink-subtle line-clamp-2">{sub}</div>
    </div>
  );
  return link ? <Link to={link} data-testid={testid}>{inner}</Link> : <div data-testid={testid}>{inner}</div>;
}
